import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import styles from './InvestmentSumup.module.css';
import { getStockNumberFromAmount } from 'utils/stock';
import { AppDispatch } from 'store/types';
import { TaxRate } from 'store/features/investments/types';
import { logError } from 'utils/logs';

interface Props {
  value: number;
  minimum: number;
  minimumPea: number;
  taxRate?: TaxRate;
  stockPrice: number;
  valuation: number;
  children?: React.ReactNode,
  disabled?: boolean;
  page?: string;
}

export default function InvestmentSumup({
  value, minimum, minimumPea, taxRate, stockPrice, valuation, children, disabled = false, page
}: Props) {
  const [, setHasError] = useState(false);
  const [isTaxIR, setIsTaxIR] = useState(window.sessionStorage.getItem('pea') === null || window.sessionStorage.getItem('pea') === 'false');

  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();

  const getStockNumber = () => {
    return getStockNumberFromAmount(stockPrice, value);
  };

  const getTaxReduction = (): number => {
    try {
      if (value) {
        return value * 25 / 100;
      }
      else {
        return 0;
      }
    } catch (err) {
      logError(err as Error);
      return 0;
    }
  };

  const getSocietyPercentage = () => {
    try {
      if (valuation && stockPrice && value) {
        return ((100 * getStockNumber() * stockPrice) / valuation).toFixed(2);
      }
      return 0;
    } catch (err) {
      logError(err as Error);
      return 0;
    }
  };

  const handleTaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const bool = (e.target.name === 'IR') ? e.target.checked : !e.target.checked;
    setIsTaxIR(bool);
    window.sessionStorage.setItem('pea', `${!bool}`);
    dispatch({ type: 'UPDATE_PEA', payload: !bool });
    if (bool) {
      setHasError(value < minimum);
    } else {
      if (value < minimumPea) {
        setHasError(true);
      }
    }
  }

  return (
    <div className={`${styles.details} ${(page && page.trim() !== '') ? styles[page] : ''}`}>
      <p className={styles.title}> {t('payment.details.title')} </p>
      <div className={`${styles.grid} ${styles.separator}`}>
        <p className={styles.subtle}>
          {t('investments.amount.details')}
        </p>
        <p className={styles.right} data-cy="investment-amount">
          {value.toLocaleString(undefined, { minimumFractionDigits: 2 })}€
        </p>
        <p className={styles.subtle}>
          {t('payment.details.actions')}
        </p>
        <p className={styles.right} data-cy="shares-number">
          {(value / stockPrice).toLocaleString(undefined, { minimumFractionDigits: 0 })}
        </p>
        <p className={styles.subtle}>
          {t('payment.details.price')}
        </p>
        <p className={styles.right} data-cy="share-price">
          {stockPrice?.toLocaleString(undefined, { minimumFractionDigits: 2 })}€
        </p>
        <p className={styles.subtle}>
          {t('payment.details.society')}
        </p>
        <p className={styles.right} data-cy="society-percentage">
          {getSocietyPercentage()}%
        </p>
      </div>
      {
        (taxRate)
        && (
          <div className={`${styles.grid} ${styles.separator}`}>
            <p className={styles.subtle}>
              {t('payment.details.taxType')}
            </p>
            {
              (!disabled)
                ? (
                  <div className={`${styles.row} ${styles.radioContainer} ${styles.rightContent}`}>
                    <div
                      className={`${styles.row} ${!taxRate?.ir ? styles.hidden : ''}`}
                      hidden={!taxRate?.ir}
                    >
                      <input
                        type="radio"
                        name="IR"
                        className={styles.radio}
                        checked={isTaxIR}
                        onChange={handleTaxChange}
                        data-cy="tax-ir"
                      />
                      <p>IR</p>
                    </div>
                    <div
                      className={`${styles.row} ${(!taxRate?.pea && !taxRate?.pea_pme) ? styles.hidden : ''}`}
                      hidden={!taxRate?.pea || !taxRate?.pea_pme}
                    >
                      <input
                        type="radio"
                        name="PEA"
                        className={styles.radio}
                        checked={!isTaxIR}
                        onChange={handleTaxChange}
                        data-cy="tax-pea"
                      />
                      <p>PEA</p>
                    </div>
                  </div>
                )
                : (
                  <div className={`${styles.row} ${styles.radioContainer} ${styles.rightContent}`}>
                    {
                      (isTaxIR)
                        ? (
                          <div className={`${styles.row} ${!taxRate?.ir ? styles.hidden : ''}`} hidden={!taxRate?.ir}>
                            <p>IR</p>
                          </div>
                        )
                        : (
                          <div className={`${styles.row} ${(!taxRate?.pea && !taxRate?.pea_pme) ? styles.hidden : ''}`} hidden={!taxRate?.pea || !taxRate?.pea_pme}>
                            <p>PEA</p>
                          </div>
                        )
                    }
                  </div>
                )
            }
            {
              (isTaxIR)
              && (
                <>
                  <p className={styles.subtle}>
                    {t('payment.details.taxReduction')}
                  </p>
                  <p className={styles.right} data-cy="tax-reduction">
                    {getTaxReduction().toLocaleString(undefined, { minimumFractionDigits: 2 })}€
                  </p>
                </>
              )
            }
          </div>
        )
      }
      <div className={styles.grid}>
        <p className={styles.total}>Total :</p>
        <p
          className={styles.totalValue}
          data-cy="total-investment"
        >
          {value.toLocaleString(undefined, { minimumFractionDigits: 2 })}€
        </p>
      </div>
      {children}
    </div>
  );
}
